/* tslint:disable: jsx-no-lambda */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import InfoIcon from '@material-ui/icons/Info';
import swal from 'sweetalert';
import { getTasas, createTasa, updateTasa, deleteTasa } from 'services/TasasCrediSeguroService';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(4),
    backgroundColor: '#ffffff',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  pageTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1e293b',
  },
  addButton: {
    backgroundColor: '#02CBF4',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#02a8d4',
    },
    borderRadius: 8,
    padding: '8px 20px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&:disabled': {
      backgroundColor: 'rgba(2, 203, 244, 0.4)',
      color: '#ffffff',
    },
  },
  card: {
    borderRadius: 16,
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(0, 0, 0, 0.03)',
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    color: '#02CBF4',
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow: 'none',
    border: '1px solid rgba(0, 0, 0, 0.05)',
  },
  tableHead: {
    backgroundColor: '#f8fafc',
  },
  tableHeaderCell: {
    color: '#02CBF4',
    fontWeight: 600,
    fontSize: '0.8125rem',
    borderBottom: '2px solid rgba(0, 0, 0, 0.04)',
    whiteSpace: 'nowrap',
  },
  tableCell: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.03)',
    padding: theme.spacing(1.75, 2),
    fontSize: '0.875rem',
    color: '#1e293b',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: '#94a3b8',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
  kpiLoading: {
    color: '#02CBF4',
  },
  actionButton: {
    padding: 6,
    color: '#94a3b8',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  },
  editIcon: {
    '&:hover': {
      color: '#02CBF4',
    },
  },
  deleteIcon: {
    '&:hover': {
      color: '#E74C3C',
    },
  },
  rateValue: {
    fontWeight: 600,
    color: '#10B981',
  },
  rangeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.875rem',
    color: '#475569',
  },
  rangeSeparator: {
    color: '#94a3b8',
    fontWeight: 400,
  },
  dialogPaper: {
    borderRadius: 16,
    padding: theme.spacing(1),
    minWidth: 480,
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
    '& h2': {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#1e293b',
    },
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2.5),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#f8fafc',
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#02CBF4',
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    '& label.Mui-focused': {
      color: '#02CBF4',
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  cancelButton: {
    color: '#64748b',
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  },
  saveButton: {
    backgroundColor: '#02CBF4',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#02a8d4',
    },
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&:disabled': {
      backgroundColor: 'rgba(2, 203, 244, 0.4)',
      color: '#ffffff',
    },
  },
  fieldError: {
    color: '#E74C3C',
    fontSize: '0.75rem',
    marginTop: 4,
    marginLeft: 4,
  },
  infoLegend: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  infoText: {
    fontSize: '0.8125rem',
    color: '#64748b',
    lineHeight: 1.5,
  },
  infoIcon: {
    color: '#02CBF4',
    fontSize: '1.1rem',
    marginTop: 1,
    flexShrink: 0,
  },
  overlapWarning: {
    color: '#F59E0B',
    fontSize: '0.75rem',
    marginTop: 4,
    marginLeft: 4,
  },
}));

const formatCOP = (value: number) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(value);

const formatRate = (rate: number) => `${(rate * 100).toFixed(2)}%`;

interface FormState {
  min_value: string;
  max_value: string;
  interest_rate_display: string;
  price_gateway: string;
}

interface FormErrors {
  min_value?: string;
  max_value?: string;
  interest_rate_display?: string;
  price_gateway?: string;
  overlap?: string;
}

const TasasCrediSeguro: React.FC = () => {
  const classes = useStyles();

  const [tasas, setTasas] = useState<ITasaCrediSeguro[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [form, setForm] = useState<FormState>({
    min_value: '',
    max_value: '',
    interest_rate_display: '',
    price_gateway: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const loadTasas = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTasas();
      setTasas(data.tasas || []);
    } catch (err) {
      setError('Error al cargar las tasas. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasas();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ min_value: '', max_value: '', interest_rate_display: '', price_gateway: '' });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (tasa: ITasaCrediSeguro) => {
    setEditingId(tasa.id);
    setForm({
      min_value: String(tasa.min_value),
      max_value: String(tasa.max_value),
      interest_rate_display: String((tasa.interest_rate * 100).toFixed(2)),
      price_gateway: String(tasa.price_gateway),
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setFormErrors({});
  };

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined, overlap: undefined }));
  };

  const checkOverlap = (minVal: number, maxVal: number): boolean => {
    return tasas.some((t) => {
      if (editingId !== null && t.id === editingId) return false;
      return minVal <= t.max_value && maxVal >= t.min_value;
    });
  };

  const validate = (): boolean => {
    const errors: FormErrors = {};

    const min = Number(form.min_value);
    const max = Number(form.max_value);
    const rate = Number(form.interest_rate_display);
    const gateway = Number(form.price_gateway);

    if (!form.min_value || isNaN(min) || min < 0) {
      errors.min_value = 'Ingresa un valor mínimo válido (mayor o igual a 0)';
    }
    if (!form.max_value || isNaN(max) || max <= 0) {
      errors.max_value = 'Ingresa un valor máximo válido (mayor a 0)';
    }
    if (!errors.min_value && !errors.max_value && min >= max) {
      errors.max_value = 'El valor máximo debe ser mayor al valor mínimo';
    }
    if (!form.interest_rate_display || isNaN(rate) || rate <= 0 || rate > 100) {
      errors.interest_rate_display = 'Ingresa una tasa válida entre 0 y 100';
    }
    if (!form.price_gateway || isNaN(gateway) || gateway < 0) {
      errors.price_gateway = 'Ingresa un costo de pasarela válido';
    }

    if (!errors.min_value && !errors.max_value && checkOverlap(min, max)) {
      errors.overlap = 'Este rango se superpone con una tasa existente. Verifica los valores.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload: ITasaCrediSeguroPayload = {
      min_value: Number(form.min_value),
      max_value: Number(form.max_value),
      interest_rate: Number(form.interest_rate_display) / 100,
      price_gateway: Number(form.price_gateway),
    };

    try {
      setSaving(true);
      if (editingId !== null) {
        await updateTasa(editingId, payload);
      } else {
        await createTasa(payload);
      }
      setDialogOpen(false);
      await loadTasas();
      swal({
        title: editingId !== null ? 'Tasa actualizada' : 'Tasa creada',
        text:
          editingId !== null
            ? 'La tasa ha sido actualizada correctamente.'
            : 'La nueva tasa ha sido creada correctamente.',
        icon: 'success',
      } as any);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'No se pudo guardar la tasa. Intenta de nuevo.';
      swal({ title: 'Error', text: message, icon: 'error' } as any);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tasa: ITasaCrediSeguro) => {
    const confirm = await swal({
      title: '¿Eliminar tasa?',
      text: `¿Estás seguro de que deseas eliminar el rango ${formatCOP(
        tasa.min_value
      )} — ${formatCOP(tasa.max_value)}? Esta acción no se puede deshacer.`,
      icon: 'warning',
      buttons: ['Cancelar', 'Eliminar'],
      dangerMode: true,
    } as any);

    if (!confirm) return;

    try {
      await deleteTasa(tasa.id);
      await loadTasas();
      swal({
        title: 'Eliminada',
        text: 'La tasa fue eliminada correctamente.',
        icon: 'success',
      } as any);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo eliminar la tasa.';
      swal({ title: 'Error', text: message, icon: 'error' } as any);
    }
  };

  const sortedTasas = [...tasas].sort((a, b) => a.min_value - b.min_value);

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.pageTitle}>Tasas CrediSeguro</Typography>
        <Button
          variant="contained"
          className={classes.addButton}
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={loading}>
          Nueva Tasa
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.sectionTitle}>Rangos y Tasas Configuradas</Typography>

              {loading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress size={32} className={classes.kpiLoading} />
                </div>
              ) : error ? (
                <Box className={classes.emptyState}>
                  <Typography style={{ color: '#E74C3C', marginBottom: 12 }}>{error}</Typography>
                  <Button
                    variant="outlined"
                    onClick={loadTasas}
                    style={{
                      borderColor: '#02CBF4',
                      color: '#02CBF4',
                      borderRadius: 8,
                      textTransform: 'none',
                    }}>
                    Reintentar
                  </Button>
                </Box>
              ) : (
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell className={classes.tableHeaderCell}>
                          Rango Financiado (COP)
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} align="right">
                          Tasa de Interés
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} align="right">
                          Costo Pasarela
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} align="right">
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedTasas.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" className={classes.tableCell}>
                            <Typography className={classes.emptyState}>
                              No hay tasas configuradas. Crea la primera.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedTasas.map((tasa) => (
                          <TableRow key={tasa.id} className={classes.tableRow}>
                            <TableCell className={classes.tableCell}>
                              <span className={classes.rangeBadge}>
                                {formatCOP(tasa.min_value)}
                                <span className={classes.rangeSeparator}>&nbsp;—&nbsp;</span>
                                {formatCOP(tasa.max_value)}
                              </span>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              <span className={classes.rateValue}>
                                {formatRate(tasa.interest_rate)}
                              </span>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              {formatCOP(tasa.price_gateway)}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              <Tooltip title="Editar" arrow>
                                <IconButton
                                  className={`${classes.actionButton} ${classes.editIcon}`}
                                  size="small"
                                  onClick={() => openEdit(tasa)}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar" arrow>
                                <IconButton
                                  className={`${classes.actionButton} ${classes.deleteIcon}`}
                                  size="small"
                                  onClick={() => handleDelete(tasa)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box className={classes.infoLegend}>
                <InfoIcon className={classes.infoIcon} />
                <Typography className={classes.infoText}>
                  Los valores están en pesos colombianos (COP). La tasa de interés se ingresa como
                  porcentaje (ej: 1.85 equivale a 1.85%).
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="sm"
        fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          {editingId !== null ? 'Editar Tasa' : 'Nueva Tasa'}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                className={classes.formField}
                label="Valor mínimo (COP)"
                variant="outlined"
                fullWidth
                type="number"
                value={form.min_value}
                onChange={handleChange('min_value')}
                error={!!formErrors.min_value}
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              {formErrors.min_value && (
                <FormHelperText className={classes.fieldError}>
                  {formErrors.min_value}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.formField}
                label="Valor máximo (COP)"
                variant="outlined"
                fullWidth
                type="number"
                value={form.max_value}
                onChange={handleChange('max_value')}
                error={!!formErrors.max_value}
                inputProps={{ min: 1 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              {formErrors.max_value && (
                <FormHelperText className={classes.fieldError}>
                  {formErrors.max_value}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.formField}
                label="Tasa de interés"
                variant="outlined"
                fullWidth
                type="number"
                value={form.interest_rate_display}
                onChange={handleChange('interest_rate_display')}
                error={!!formErrors.interest_rate_display}
                inputProps={{ min: 0, max: 100, step: '0.0001' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              {formErrors.interest_rate_display && (
                <FormHelperText className={classes.fieldError}>
                  {formErrors.interest_rate_display}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.formField}
                label="Costo pasarela (COP)"
                variant="outlined"
                fullWidth
                type="number"
                value={form.price_gateway}
                onChange={handleChange('price_gateway')}
                error={!!formErrors.price_gateway}
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              {formErrors.price_gateway && (
                <FormHelperText className={classes.fieldError}>
                  {formErrors.price_gateway}
                </FormHelperText>
              )}
            </Grid>
            {formErrors.overlap && (
              <Grid item xs={12}>
                <Box
                  style={{
                    backgroundColor: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 8,
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}>
                  <InfoIcon
                    style={{ color: '#F59E0B', fontSize: '1rem', marginTop: 2, flexShrink: 0 }}
                  />
                  <Typography className={classes.overlapWarning} style={{ marginTop: 0 }}>
                    {formErrors.overlap}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={closeDialog} className={classes.cancelButton} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            className={classes.saveButton}
            variant="contained"
            disabled={saving}>
            {saving ? (
              <CircularProgress size={18} style={{ color: '#fff' }} />
            ) : editingId !== null ? (
              'Guardar cambios'
            ) : (
              'Crear tasa'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TasasCrediSeguro;
